// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * Team Management Service
 * 
 * API client for team and team member operations.
 */

import apiClient from '../config/api';

const BASE_URL = '/api/v1/teams/';

const teamManagementService = {
  // ============================================================================
  // Team Operations
  // ============================================================================
  
  /**
   * List all teams accessible by current user
   */
  listTeams: async (includeInactive = false) => {
    const params = includeInactive ? { include_inactive: true } : {};
    const response = await apiClient.get(BASE_URL, { params });
    return response.data;
  },
  
  /**
   * Get team details by ID
   */
  getTeam: async (teamId) => {
    const response = await apiClient.get(`${BASE_URL}${teamId}`);
    return response.data;
  },
  
  /**
   * Create new team
   */
  createTeam: async (teamData) => {
    const response = await apiClient.post(BASE_URL, teamData);
    return response.data;
  },
  
  /**
   * Update team
   */
  updateTeam: async (teamId, teamData) => {
    const response = await apiClient.put(`${BASE_URL}${teamId}`, teamData);
    return response.data;
  },
  
  /**
   * Delete team
   */
  deleteTeam: async (teamId) => {
    const response = await apiClient.delete(`${BASE_URL}${teamId}`);
    return response.data;
  },

  /**
   * Reactivate team
   */
  reactivateTeam: async (teamId) => {
    const response = await apiClient.patch(`${BASE_URL}${teamId}/reactivate`);
    return response.data;
  },
  
  /**
   * Bulk remove team members
   */
  bulkRemoveMembers: async (teamId, userIds) => {
    const response = await apiClient.post(`${BASE_URL}${teamId}/members/bulk-remove`, {
      user_ids: userIds
    });
    return response.data;
  },
  
  /**
   * Get team statistics
   */
  getTeamStats: async (teamId) => {
    const response = await apiClient.get(`${BASE_URL}${teamId}/stats`);
    return response.data;
  },
  
  // ============================================================================
  // Team Member Operations
  // ============================================================================
  
  /**
   * List team members
   */
  listMembers: async (teamId) => {
    const response = await apiClient.get(`${BASE_URL}${teamId}/members`);
    return response.data;
  },
  
  /**
   * Add team members
   */
  addMembers: async (teamId, memberData) => {
    const response = await apiClient.post(`${BASE_URL}${teamId}/members`, memberData);
    return response.data;
  },
  
  /**
   * Update team member
   */
  updateMember: async (teamId, userId, memberData) => {
    const response = await apiClient.put(`${BASE_URL}${teamId}/members/${userId}`, memberData);
    return response.data;
  },
  
  /**
   * Remove team member
   */
  removeMember: async (teamId, userId) => {
    const response = await apiClient.delete(`${BASE_URL}${teamId}/members/${userId}`);
    return response.data;
  }
};

export default teamManagementService;

